import type { Locale } from '@/app/lib/i18n'

// Inline SVG flags for the language switcher. Emoji flags (🇪🇸) aren't an
// option: Windows has no flag glyphs and renders them as bare letter pairs
// ("ES"). Every flag is drawn on the same 30x20 (3:2) box so they line up,
// simplified to what's legible at ~20px wide (no coats of arms, a dot grid
// instead of 50 stars).

// Points of a 5-pointed star centred on (cx, cy), first point aimed at
// `angle` degrees (0 = straight up).
function starPoints(cx: number, cy: number, r: number, angle = 0): string {
  const points: string[] = []
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? r : r * 0.382
    const theta = ((angle + i * 36) * Math.PI) / 180
    points.push(`${(cx + radius * Math.sin(theta)).toFixed(2)},${(cy - radius * Math.cos(theta)).toFixed(2)}`)
  }
  return points.join(' ')
}

const stripeHeight = 20 / 13

function UnitedStates() {
  return (
    <>
      <rect width="30" height="20" fill="#B22234" />
      {[1, 3, 5, 7, 9, 11].map((i) => (
        <rect key={i} y={i * stripeHeight} width="30" height={stripeHeight} fill="#fff" />
      ))}
      <rect width="12" height={7 * stripeHeight} fill="#3C3B6E" />
      {[0, 1, 2, 3].map((row) =>
        [0, 1, 2, 3, 4].map((col) => (
          <circle key={`${row}-${col}`} cx={1.4 + col * 2.3} cy={1.5 + row * 2.6} r="0.5" fill="#fff" />
        ))
      )}
    </>
  )
}

function Spain() {
  return (
    <>
      <rect width="30" height="20" fill="#AA151B" />
      <rect y="5" width="30" height="10" fill="#F1BF00" />
    </>
  )
}

function Brazil() {
  return (
    <>
      <rect width="30" height="20" fill="#009C3B" />
      <polygon points="15,2.2 27.6,10 15,17.8 2.4,10" fill="#FFDF00" />
      <circle cx="15" cy="10" r="4.6" fill="#002776" />
      <path d="M10.6 9.2 Q15 7.8 19.5 10.8" stroke="#fff" strokeWidth="0.9" fill="none" />
    </>
  )
}

function China() {
  return (
    <>
      <rect width="30" height="20" fill="#EE1C25" />
      <polygon points={starPoints(5, 5, 3)} fill="#FFFF00" />
      <polygon points={starPoints(10, 2, 1, 23)} fill="#FFFF00" />
      <polygon points={starPoints(12, 4, 1, 45)} fill="#FFFF00" />
      <polygon points={starPoints(12, 7, 1, 70)} fill="#FFFF00" />
      <polygon points={starPoints(10, 9, 1, 20)} fill="#FFFF00" />
    </>
  )
}

function Japan() {
  return (
    <>
      <rect width="30" height="20" fill="#fff" />
      <circle cx="15" cy="10" r="6" fill="#BC002D" />
    </>
  )
}

function France() {
  return (
    <>
      <rect width="10" height="20" fill="#0055A4" />
      <rect x="10" width="10" height="20" fill="#fff" />
      <rect x="20" width="10" height="20" fill="#EF4135" />
    </>
  )
}

// Saudi Arabia, for the Arabic locale (its launch audience). The shahada is
// far too detailed to draw at 21px, so it's a single wavy white stroke that
// reads as script, over the sword.
function SaudiArabia() {
  return (
    <>
      <rect width="30" height="20" fill="#006C35" />
      <path
        d="M8 8.5 q1.4 -2.2 2.8 0 t2.8 0 t2.8 0 t2.8 0 t2.8 0"
        fill="none"
        stroke="#fff"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
      <rect x="9" y="12.6" width="12" height="0.9" fill="#fff" />
      <rect x="20.2" y="11.9" width="0.9" height="2.3" fill="#fff" />
    </>
  )
}

const flags: Record<Locale, () => React.JSX.Element> = {
  en: UnitedStates,
  es: Spain,
  'pt-BR': Brazil,
  'zh-CN': China,
  ja: Japan,
  fr: France,
  ar: SaudiArabia,
}

export default function Flag({ locale, className }: { locale: Locale; className?: string }) {
  const Drawing = flags[locale]
  // The span does the rounding/outline: border-radius on an inline <svg>
  // doesn't reliably clip its content across browsers.
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        flexShrink: 0,
        borderRadius: '2px',
        overflow: 'hidden',
        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.15)',
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 30 20" width="21" height="14" style={{ display: 'block' }}>
        <Drawing />
      </svg>
    </span>
  )
}
